// ⛔ A NOTICE SCAN CHANGES NOTHING ON THE PARCEL UNLESS THE PERSON TICKS IT (Philip 2026-11-03).
//
// The scanner used to write every field the notice carried straight over the parcel — owner, address, legal
// description, land area, building area, year built, the account number, the base appeal status ('Under Review',
// hardcoded), the base notice date and deadline, the year's status row and the year's Proposed values — with no
// choice offered. The one question it did ask (the account number, confirmScannedAcct) was skipped whenever EITHER of
// the notice's two numbers matched the parcel, and the save then wrote the OTHER number over the parcel's account:
// Dessau Rd 258879 → 2067013 and Tremont Rd 6071201005 → 60712 (docs/scanner-overwrites-2026-11-03.md).
//
// This lists, from what is on file and what the save would write, every change the save would make — each one
// becomes a tick box, unticked. A field the notice leaves blank, or that already holds the same value, is not a
// change and is not listed. The notice's own record (parcel_values) and the document filing are not parcel changes
// and always save.
//
// Pure: no database. → [{ id, group, label, from, to }]   group: 'parcel' | 'year' | 'proposed' | 'status'

const BLANK = '(blank)';

const PARCEL_FIELDS = [
  ['owner_name', 'Owner'],
  ['address', 'Address'],
  ['legal_description', 'Legal description'],
  ['account_number', 'Account number'],
  ['land_area_sqft', 'Land area (sq ft)'],
  ['assessor_bldg_sqft', 'Building sq ft'],
  ['year_built', 'Year built'],
  ['notice_date', 'Notice date'],
  ['appeal_deadline', 'Appeal deadline'],
  ['appeal_status', 'Appeal status'],
];
const YEAR_FIELDS = [
  ['owner_name', 'Owner'],
  ['account_number', 'Account number'],
  ['assessor_bldg_sqft', 'Building sq ft'],
  ['rate_lines', 'Rate table'],
];
// The Proposed figures the parcel card shows; the rest of the row (is_primary, nulls) follows them.
const PROPOSED_FIELDS = [
  ['market_land', 'Land'], ['market_imp', 'Improvements'], ['market_total', 'Market'], ['cap_deduction', 'Cap'],
  ['less_exemption_amount', 'Exemption'], ['taxable_subtotal', 'Taxable subtotal'],
  ['less_exemptions_amount', 'Exemptions'], ['taxable_total', 'Taxable'],
];

const isNum = (v) => v !== '' && v != null && Number.isFinite(Number(v));
const shown = (v) => {
  if (v == null || String(v).trim() === '') return BLANK;
  if (typeof v === 'number') return v.toLocaleString('en-US');
  return String(v).replace(/\s+/g, ' ').trim();
};
// Same value: numbers by number (215534.88 vs 215535 is a change; '1996' vs 1996 is not), text by its exact
// trimmed characters — a tab, a zip code appended or a dropped 'C/O' is a change the person should see.
export function sameValue(a, b) {
  const blankA = a == null || String(a).trim() === '', blankB = b == null || String(b).trim() === '';
  if (blankA || blankB) return blankA && blankB;
  if (isNum(a) && isNum(b)) return Number(a) === Number(b);
  return String(a).trim() === String(b).trim();
}
const linesShown = (l) => (Array.isArray(l) && l.length ? `${l.length} line${l.length === 1 ? '' : 's'}` : BLANK);

// year: the notice year · parcel: the parcel row on file · yearRow: that year's parcel_year_data row (or null) ·
// proposed: that year's Proposed parcel_appeals rows on file · statusRow: that year's parcel_status row (or null) ·
// write: what the save would write — { parcel: {…}, year: {…}, proposed: [{ jurisdiction, row }], status }.
// Only keys present in write.parcel / write.year are considered, exactly as the save only sets what it captured.
export function noticeParcelChanges({ year, parcel, yearRow, proposed, statusRow, write }) {
  const items = [];
  for (const [k, label] of PARCEL_FIELDS) {
    if (!write.parcel || !(k in write.parcel)) continue;
    const to = write.parcel[k];
    if (to == null || String(to).trim() === '') continue;   // the notice left it blank — never a change
    const from = parcel ? parcel[k] : null;
    if (sameValue(from, to)) continue;
    items.push({ id: `parcel.${k}`, group: 'parcel', label, from: shown(from), to: shown(to) });
  }
  for (const [k, label] of YEAR_FIELDS) {
    if (!write.year || !(k in write.year)) continue;
    const to = write.year[k];
    const from = yearRow ? yearRow[k] : null;
    if (k === 'rate_lines') {
      if (!Array.isArray(to) || !to.length || JSON.stringify(from ?? null) === JSON.stringify(to)) continue;
      items.push({ id: 'year.rate_lines', group: 'year', label: `${year} ${label}`, from: linesShown(from), to: `${linesShown(to)} (from the notice)` });
      continue;
    }
    if (to == null || String(to).trim() === '' || sameValue(from, to)) continue;
    items.push({ id: `year.${k}`, group: 'year', label: `${year} ${label}`, from: shown(from), to: shown(to) });
  }
  for (const { jurisdiction, row } of write.proposed || []) {
    const onFile = (proposed || []).find((r) => (r.jurisdiction || '') === (jurisdiction || ''));
    const diffs = PROPOSED_FIELDS.filter(([k]) => !sameValue(onFile ? onFile[k] : null, row[k]));
    if (!diffs.length) continue;
    const fig = (r) => diffs.map(([k, l]) => `${l} ${shown(r ? r[k] : null)}`).join(' · ');
    items.push({ id: `proposed.${jurisdiction || ''}`, group: 'proposed', label: `${year} Proposed values${jurisdiction ? ` (${jurisdiction})` : ''}`,
      from: onFile ? fig(onFile) : 'none on file', to: fig(row) });
  }
  if (write.status && !sameValue(statusRow ? statusRow.status : null, write.status)) {
    items.push({ id: 'status', group: 'status', label: `${year} Parcel status`, from: shown(statusRow ? statusRow.status : null), to: shown(write.status) });
  }
  return items;
}
